import React, { useState } from "react";
import {
  Box, Card, Typography, Chip,
  Table, TableHead, TableRow, TableCell, TableBody, Snackbar, Alert,
  CircularProgress, Skeleton, TextField, InputAdornment,
  Tooltip, IconButton,
} from "@mui/material";
import { FileText, Calendar, User, Database, Hash, Eye, Search, Clock } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import AdminShell from "../layout/AdminShell";
import { getAuditLogs } from "../../api/metrics";

// Helper components for action icons
const ActionPlusIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19"></line>
    <line x1="5" y1="12" x2="19" y2="12"></line>
  </svg>
);

const ActionPencilIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
  </svg>
);

const ActionTrashIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#DC2626" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 6h18"></path><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
  </svg>
);

const ActionLoginIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#0891B2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"></path>
    <polyline points="10 17 15 12 10 7"></polyline>
    <line x1="15" y1="12" x2="3" y2="12"></line>
  </svg>
);

const ActionLogoutIcon = ({ size = 12 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
    <polyline points="16 17 21 12 16 7"></polyline>
    <line x1="21" y1="12" x2="9" y2="12"></line>
  </svg>
);

const ACTION_STYLES = {
  CREATED: { color: "success", bg: "rgba(34,197,94,0.1)", labelColor: "#16A34A" },
  UPDATED: { color: "primary", bg: "rgba(37,99,235,0.1)", labelColor: "#2563EB" },
  DELETED: { color: "error", bg: "rgba(239,68,68,0.1)", labelColor: "#DC2626" },
  LOGIN: { color: "info", bg: "rgba(6,182,212,0.1)", labelColor: "#0891B2" },
  LOGOUT: { color: "default", bg: "rgba(107,114,128,0.1)", labelColor: "#6B7280" },
};

const ACTION_ICONS = {
  CREATED: ActionPlusIcon,
  UPDATED: ActionPencilIcon,
  DELETED: ActionTrashIcon,
  LOGIN: ActionLoginIcon,
  LOGOUT: ActionLogoutIcon,
};


export default function AuditLogsPage() {
  const [searchQuery, setSearchQuery] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [modelFilter, setModelFilter] = useState("");

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["audit-logs"],
    queryFn: () => getAuditLogs().then(r => r.data),
  });

  const handleSearch = (e) => setSearchQuery(e.target.value);
  const handleActionFilter = (action) => setActionFilter(action === actionFilter ? "" : action);
  const handleModelFilter = (model) => setModelFilter(model === modelFilter ? "" : model);

  // Get unique values for filters
  const allActions = [...new Set(logs.map(l => l.action))];
  const allModels = [...new Set(logs.map(l => l.model_name))];

  // Filter logs
  const filteredLogs = logs.filter(l => {
    const matchesSearch = 
      l.user_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      l.action?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      l.model_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      l.object_id?.toString().includes(searchQuery.toLowerCase()) ||
      l.ip_address?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesAction = !actionFilter || l.action === actionFilter;
    const matchesModel = !modelFilter || l.model_name === modelFilter;
    
    return matchesSearch && matchesAction && matchesModel;
  });

  return (
    <AdminShell title="Audit Logs">
      <Box sx={{ mb: 2.5 }}>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary", maxWidth: 520 }}>
          View system activity and user actions. Shows the most recent 100 audit log entries.
        </Typography>
      </Box>

      {/* Search and Filters */}
      <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", overflow: "hidden", mb: 2.5 }}>
        <Box sx={{ p: 2, display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
          <Search size={18} color="#94A3B8" />
          <TextField
            fullWidth
            placeholder="Search logs by user, action, model, or object ID..."
            value={searchQuery}
            onChange={handleSearch}
            size="small"
            sx={{ flex: 1, minWidth: 250 }}
            InputProps={{ sx: { fontSize: 13.5 } }}
          />
        </Box>
        
        {/* Filter chips */}
        {allActions.length > 0 && (
          <Box sx={{ px: 2, pb: 2, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", letterSpacing: "0.05em", textTransform: "uppercase", mr: 1, alignSelf: "center" }}>
              Action:
            </Typography>
            {allActions.map(action => {
              const style = ACTION_STYLES[action] || ACTION_STYLES.UPDATED;
              const isActive = actionFilter === action;
              return (
                <Chip
                  key={action}
                  label={action}
                  size="small"
                  onClick={() => handleActionFilter(action)}
                  sx={{
                    fontWeight: 600,
                    fontSize: 11.5,
                    height: 24,
                    bgcolor: isActive ? style.bg : "transparent",
                    color: isActive ? style.labelColor : "text.secondary",
                    border: isActive ? "none" : "1px solid rgba(15,23,42,0.1)",
                    cursor: "pointer",
                    "&:hover": { bgcolor: style.bg }
                  }}
                />
              );
            })}
            {actionFilter && (
              <Chip
                label="Clear"
                size="small"
                onClick={() => setActionFilter("")}
                sx={{ fontWeight: 600, fontSize: 11.5, height: 24, color: "#6B7280", border: "1px solid rgba(15,23,42,0.1)", cursor: "pointer" }}
              />
            )}
          </Box>
        )}
        
        {allModels.length > 0 && (
          <Box sx={{ px: 2, pb: 2, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary", letterSpacing: "0.05em", textTransform: "uppercase", mr: 1, alignSelf: "center" }}>
              Model:
            </Typography>
            {allModels.map(model => {
              const isActive = modelFilter === model;
              return (
                <Chip
                  key={model}
                  label={model}
                  size="small"
                  onClick={() => handleModelFilter(model)}
                  sx={{
                    fontWeight: 600,
                    fontSize: 11.5,
                    height: 24,
                    bgcolor: isActive ? "rgba(37,99,235,0.1)" : "transparent",
                    color: isActive ? "#2563EB" : "text.secondary",
                    border: isActive ? "none" : "1px solid rgba(15,23,42,0.1)",
                    cursor: "pointer",
                    "&:hover": { bgcolor: "rgba(37,99,235,0.08)" }
                  }}
                />
              );
            })}
            {modelFilter && (
              <Chip
                label="Clear"
                size="small"
                onClick={() => setModelFilter("")}
                sx={{ fontWeight: 600, fontSize: 11.5, height: 24, color: "#6B7280", border: "1px solid rgba(15,23,42,0.1)", cursor: "pointer" }}
              />
            )}
          </Box>
        )}
      </Card>

      {/* Results count */}
      {!isLoading && filteredLogs.length > 0 && (
        <Box sx={{ mb: 1.5 }}>
          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            Showing {filteredLogs.length} of {logs.length} entries
          </Typography>
        </Box>
      )}

      {/* Audit Logs Table */}
      <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", boxShadow: "0 1px 2px rgba(15,23,42,0.04)", overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                {["User", "Action", "Model", "Object ID", "IP Address", "When"].map(h => (
                  <TableCell key={h} sx={{ fontWeight: 700, fontSize: 12, letterSpacing: "0.03em", textTransform: "uppercase", color: "text.secondary", borderBottom: "1px solid rgba(15,23,42,0.08)", whiteSpace: "nowrap" }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j}><Skeleton variant="text" width={j === 0 ? 120 : j === 1 ? 80 : j === 5 ? 140 : 100} /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : filteredLogs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: "14px", bgcolor: "rgba(37,99,235,0.07)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 1.5 }}>
                      <FileText size={22} color="#2563EB" />
                    </Box>
                    <Typography fontWeight={700} color="#0F172A" mb={0.5}>No audit logs found</Typography>
                    <Typography color="text.secondary" fontSize={13.5}>
                      {searchQuery || actionFilter || modelFilter ? "Try adjusting your filters." : "Audit logs will appear here as actions are performed."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : filteredLogs.map((l) => {
                const actionStyle = ACTION_STYLES[l.action] || ACTION_STYLES.UPDATED;
                const IconComponent = ACTION_ICONS[l.action];
                
                return (
                  <TableRow key={l.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    {/* User */}
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Box sx={{ width: 32, height: 32, borderRadius: "8px", bgcolor: "rgba(37,99,235,0.08)", color: "#2563EB", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                          <User size={14} />
                        </Box>
                        <Typography sx={{ fontWeight: 600, fontSize: 13, color: "#0F172A" }}>
                          {l.user_name || "System"}
                        </Typography>
                      </Box>
                    </TableCell>

                    {/* Action */}
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Chip
                        label={l.action}
                        size="small"
                        icon={IconComponent ? <IconComponent /> : null}
                        sx={{ 
                          fontWeight: 700, 
                          fontSize: 11, 
                          height: 22,
                          bgcolor: actionStyle.bg,
                          color: actionStyle.labelColor
                        }}
                      />
                    </TableCell>

                    {/* Model */}
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Chip
                        label={l.model_name || "—"}
                        size="small"
                        sx={{ 
                          fontWeight: 600, 
                          fontSize: 11, 
                          height: 20,
                          bgcolor: "rgba(37,99,235,0.07)",
                          color: "#2563EB"
                        }}
                      />
                    </TableCell>

                    {/* Object ID */}
                    <TableCell sx={{ fontSize: 13, color: "text.secondary", whiteSpace: "nowrap" }}>
                      {l.object_id || "—"}
                    </TableCell>

                    {/* IP Address */}
                    <TableCell sx={{ fontSize: 13, color: "text.secondary", whiteSpace: "nowrap" }}>
                      {l.ip_address || "—"}
                    </TableCell>

                    {/* When */}
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Clock size={14} color="#94A3B8" />
                        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
                          {dayjs(l.created_at).format("DD MMM YYYY, h:mm A")}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Card>

      {/* Snackbar for copy notifications */}
      <Snackbar open={false} autoHideDuration={3500} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity="success" variant="filled" sx={{ borderRadius: "10px", fontWeight: 600, fontSize: 13.5 }}>
          Copied to clipboard
        </Alert>
      </Snackbar>
    </AdminShell>
  );
}